import React, { ChangeEvent, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle } from 'react-icons/fi';

import {
  Box,
  Button,
  Flex,
  Radio,
  SelectField,
  Spinner,
  useToastContext,
} from '@resideo/blueprint-react';
import { DataRequestType } from '@resideo/web-integration-services-api-client';

import ModalHeader from 'components/common/ModalHeader';
import Typography from 'components/common/Typography';
import { RisError } from 'types/types';

import { useFeatureFlags } from 'context/FeatureFlags';
import { useUserDevices } from 'hooks/useRequestData';
import { useRisClient } from 'hooks/useRisClient';

const { Text, Title } = Typography;

enum Steps {
  DATA_REQUEST_CONFIGURATION,
  DATA_REQUEST_CONFIRMATION,
}

const DeviceSelect = ({ onDeviceChange }) => {
  const { t } = useTranslation();

  const { data: locations, isLoading } = useUserDevices();

  const handleChange = e => {
    const deviceId = e.target.value;
    const selected = Object.values(locations ?? {})
      .flat()
      .find(device => device.id === deviceId);

    onDeviceChange({ id: deviceId, isOwner: selected?.isOwner ?? false });
  };

  return (
    <>
      <SelectField
        data-test-request-my-data-select-device
        disabled={isLoading}
        label={t('privacy.requestMyData.configuration.select.label')}
        name='device'
        version='v2'
        onChange={handleChange}>
        {isLoading ? (
          <option>{t('common.loading')}</option>
        ) : (
          <>
            <option value=''>{t('common.forms.choose')}</option>

            {locations &&
              Object.entries(locations).map(([locationName, devices]) =>
                devices.length ? (
                  <optgroup key={locationName} label={locationName}>
                    {devices.map(device => (
                      <option key={device.id} value={device.id}>
                        {device.productName} ({device.deviceName})
                      </option>
                    ))}
                  </optgroup>
                ) : null
              )}
          </>
        )}
      </SelectField>
    </>
  );
};

const DataRequestConfiguration = ({ closeModal, setStep, onCloseAccount }) => {
  const { client } = useRisClient();
  const { DATA_ERASURE } = useFeatureFlags();

  const { t } = useTranslation();
  const { addToast } = useToastContext();

  const [requestType, setRequestType] = useState(DataRequestType.DEVICE_DATA);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [isSelectedDeviceOwner, setIsSelectedDeviceOwner] = useState(false);

  const handleRequestTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRequestType(e.target.value as DataRequestType);
  };

  const handleDeviceChange = ({
    id,
    isOwner,
  }: {
    id: string;
    isOwner: boolean;
  }) => {
    setSelectedDevice(id);
    setIsSelectedDeviceOwner(isOwner);
  };

  const showNotOwnerError =
    requestType === DataRequestType.DEVICE_DATA &&
    !!selectedDevice &&
    !isSelectedDeviceOwner;

  const handleError = (error: RisError) => {
    const fallbackMessage = t('privacy.requestMyData.errors.submitFailed');
    const errors = error?.body?.errors || [];
    let entityConflictHandled = false;
    if (Array.isArray(errors)) {
      errors.forEach(err => {
        if (err.code === 'ENTITY_CONFLICT') {
          addToast({
            toastType: 'Error',
            message:
              err.message || t('privacy.requestMyData.errors.entityConflict'),
          });
          entityConflictHandled = true;
        }
      });
    }
    if (!entityConflictHandled) {
      (errors || [])
        .map(err => err.message)
        .filter(Boolean)
        .forEach(message =>
          addToast({ toastType: 'Error', message: message || fallbackMessage })
        );
    }
  };

  const requestSingleDeviceData = useMutation({
    mutationKey: ['requestMyData', 'singleDevice', 'submit'],
    mutationFn: async (consumerDeviceId: string) => {
      await client.reports.reportsCreateDataRequest({
        type: DataRequestType.DEVICE_DATA,
        consumerDeviceId,
      });
    },
    onSuccess: () => {
      setStep(Steps.DATA_REQUEST_CONFIRMATION);
    },
    onError: handleError,
  });

  const requestAllData = useMutation({
    mutationKey: ['requestMyData', 'allData', 'submit'],
    mutationFn: async () => {
      await client.reports.reportsCreateDataRequest({
        type: DataRequestType.FULL_DATA,
      });
    },
    onSuccess: () => {
      setStep(Steps.DATA_REQUEST_CONFIRMATION);
    },
    onError: handleError,
  });

  const handleSubmit = (consumerDeviceId: string) => {
    if (requestType === DataRequestType.DATA_ERASURE) {
      closeModal();
      onCloseAccount();
    } else if (requestType === DataRequestType.DEVICE_DATA) {
      if (!consumerDeviceId || !isSelectedDeviceOwner) return;
      requestSingleDeviceData.mutate(consumerDeviceId);
    } else {
      requestAllData.mutate();
    }
  };

  const isPending =
    requestSingleDeviceData.isPending || requestAllData.isPending;

  return (
    <>
      <ModalHeader
        title={t('privacy.requestMyData.configuration.title')}
        closeModal={closeModal}
      />

      <Text marginBottom='medium'>
        {t('privacy.requestMyData.configuration.description')}
      </Text>

      <Box marginBottom='medium'>
        <Radio
          data-test-request-my-data-type-single
          name='dataRequestType'
          checked={requestType === DataRequestType.DEVICE_DATA}
          label={t('privacy.requestMyData.configuration.options.singleDevice')}
          value={DataRequestType.DEVICE_DATA}
          onChange={handleRequestTypeChange}
        />

        <Radio
          data-test-request-my-data-type-all
          name='dataRequestType'
          checked={requestType === DataRequestType.FULL_DATA}
          label={t('privacy.requestMyData.configuration.options.allDevices')}
          value={DataRequestType.FULL_DATA}
          onChange={handleRequestTypeChange}
        />

        {DATA_ERASURE && (
          <Radio
            data-test-request-my-data-type-delete-and-close
            name='dataRequestType'
            checked={requestType === DataRequestType.DATA_ERASURE}
            label={t(
              'privacy.requestMyData.configuration.options.deleteAndClose'
            )}
            value={DataRequestType.DATA_ERASURE}
            onChange={handleRequestTypeChange}
          />
        )}
      </Box>

      {requestType === DataRequestType.DEVICE_DATA ? (
        <>
          <DeviceSelect onDeviceChange={handleDeviceChange} />

          {showNotOwnerError && (
            <Box
              data-test-request-my-data-not-owner-error
              color='danger'
              paddingTop='small'
              role='alert'>
              {t('privacy.requestMyData.errors.notDeviceOwner', {
                defaultValue:
                  'Get and Delete device data access is only enabled for owners.',
              })}
            </Box>
          )}
        </>
      ) : null}

      <Box data-test-request-my-data-modal-action paddingTop='large'>
        <Button
          disabled={
            requestType === DataRequestType.DEVICE_DATA &&
            (!selectedDevice || !isSelectedDeviceOwner)
          }
          onClick={() => handleSubmit(selectedDevice)}
          width={1}>
          {isPending ? <Spinner /> : t('common.submit')}
        </Button>
      </Box>
    </>
  );
};

const DataRequestConfirmation = ({ closeModal }) => {
  const { t } = useTranslation();

  return (
    <>
      <Flex flexDirection='column' alignItems='center'>
        <Title level={2}>{t('privacy.requestMyData.confirmation.title')}</Title>

        <FiCheckCircle style={{ marginBlockEnd: 32 }} size={54} />

        <Text>{t('privacy.requestMyData.confirmation.description')}</Text>
      </Flex>

      <Box data-test-request-my-data-modal-action paddingTop='large'>
        <Button onClick={closeModal} width={1}>
          {t('common.done')}
        </Button>
      </Box>
    </>
  );
};

export const RequestMyDataModal = ({
  closeModal,
  onCloseAccount,
}: {
  closeModal: () => void;
  onCloseAccount: () => void;
}) => {
  const steps = {
    [Steps.DATA_REQUEST_CONFIGURATION]: DataRequestConfiguration,
    [Steps.DATA_REQUEST_CONFIRMATION]: DataRequestConfirmation,
  };

  const [step, setStep] = useState(Steps.DATA_REQUEST_CONFIGURATION);

  const Component = steps[step];

  const props = {
    closeModal,
    setStep,
    onCloseAccount,
  };

  return <Component {...props} />;
};
